/**
 * Hook to manage all rebate filter state (tags, rules, divisions)
 *
 * Consolidates filter state management from StatusTab and makes it reusable.
 * Handles:
 * - Rule tag filtering (via useRuleTagFiltering)
 * - Rule type filtering (conditional vs non-conditional)
 * - Rule filtering (explicit rule selection)
 * - Division filtering (explicit division selection)
 * - Building filter functions with AND/OR logic
 */

import { useState, useMemo, useCallback } from 'react';
import type {
  RebateRule,
  RebateBreakdown,
  DivisionWithTagsDTO,
} from '@erp/shared-types';
import { useRuleTagFiltering } from '@/hooks/useRuleTagFiltering';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import type { FilterGroup } from '@/components/rebates/FilterPanelContent';

interface UseRebateFiltersParams {
  /** Rules from forecast data (preferred) or rebateRules prop */
  rules?: Array<{
    id: string;
    isConditional?: boolean;
    tags?: Array<{ id: string; name: string }>;
  }>;
  /** Fallback rules if forecast data doesn't have rules */
  rebateRules?: RebateRule[];
  /** Available divisions */
  divisions?: DivisionWithTagsDTO[];
  /** Available tag options (from useCoreEnums) */
  availableTags?: Array<{ id: string; name: string | null }>;
}

interface UseRebateFiltersReturn {
  /** Filter functions to apply to breakdowns */
  filterFns: {
    rule?: (bd: RebateBreakdown) => boolean;
    division?: (bd: RebateBreakdown) => boolean;
  };
  /** Filter groups for UI rendering */
  filterGroups: FilterGroup[];
  /** Count of active filters */
  activeFilterCount: number;
  /** Names of active filters */
  activeFilterNames: string[];
  /** Clear all filters handler */
  clearAllFilters: () => void;
  /** Selected tag IDs (for external access if needed) */
  selectedTagIds: Set<string>;
  /** Selected rule IDs (for external access if needed) */
  selectedRuleIds: Set<string>;
  /** Selected division IDs (for external access if needed) */
  selectedDivisionIds: Set<string>;
  /** Selected conditional types (for external access if needed) */
  selectedConditionalTypes: Set<'conditional' | 'non-conditional'>;
}

/**
 * Hook to manage rebate filter state
 */
export function useRebateFilters({
  rules,
  rebateRules,
  divisions,
  availableTags = [],
}: UseRebateFiltersParams): UseRebateFiltersReturn {
  // Prefer rules from forecast data (has tags and isConditional), otherwise map from rebateRules prop
  const rulesForFiltering = useMemo(() => {
    if (rules) {
      return rules;
    }
    // Fallback: map rebateRules to expected format (without tags or isConditional)
    // Note: For fallback, we'd need to compute isConditional from rebateRules, but that's complex
    // For now, we'll just use id - conditional filtering won't work without forecast data rules
    return rebateRules?.map((r) => ({ id: r.id })) || [];
  }, [rules, rebateRules]);

  // Create ruleId -> isConditional map for conditional filtering
  const ruleIdToIsConditional = useMemo(() => {
    const map = new Map<string, boolean>();
    if (rules) {
      rules.forEach((rule) => {
        if (rule.isConditional !== undefined) {
          map.set(rule.id, rule.isConditional);
        }
      });
    }
    return map;
  }, [rules]);

  // Rule tag filtering
  const { selectedTagIds, setSelectedTagIds, filteredRuleIds } =
    useRuleTagFiltering(rulesForFiltering);

  // Filter available tags to only those that are actually available
  const availableTagIds = useMemo(() => {
    const tagIds = new Set<string>();
    rulesForFiltering.forEach((rule) => {
      if ('tags' in rule && rule.tags && Array.isArray(rule.tags)) {
        rule.tags.forEach((tag: { id: string; name: string }) => {
          tagIds.add(tag.id);
        });
      }
    });
    return tagIds;
  }, [rulesForFiltering]);

  const filteredAvailableTags = useMemo(() => {
    return availableTags.filter((tag) => availableTagIds.has(tag.id));
  }, [availableTags, availableTagIds]);

  // Convert tags to MultiSelectOption format
  const tagOptions: MultiSelectOption[] = useMemo(() => {
    return filteredAvailableTags.map((tag) => ({
      id: tag.id,
      label: tag.name || 'Unnamed Tag',
    }));
  }, [filteredAvailableTags]);

  // Division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    return (
      divisions?.map((division) => ({
        id: division.id,
        label: division.name,
        value: division,
        group:
          division.tags && division.tags.length > 0
            ? division.tags[0]?.name
            : undefined,
      })) || []
    );
  }, [divisions]);

  // Rule options
  const ruleOptions: MultiSelectOption[] = useMemo(() => {
    return (
      rebateRules?.map((rule) => ({
        id: rule.id,
        label: rule.name,
        value: rule,
      })) || []
    );
  }, [rebateRules]);

  // Rule Type options (conditional vs non-conditional)
  const ruleTypeOptions: MultiSelectOption[] = useMemo(() => {
    return [
      { id: '__conditional__', label: 'Conditional' },
      { id: '__non_conditional__', label: 'Non-Conditional' },
    ];
  }, []);

  // Rule filtering state
  const [selectedRuleIds, setSelectedRuleIds] = useState<Set<string>>(
    new Set(),
  );

  // Division filtering state
  const [selectedDivisionIds, setSelectedDivisionIds] = useState<Set<string>>(
    new Set(),
  );

  // Conditional/non-conditional filtering state (default to both selected)
  const [selectedConditionalTypes, setSelectedConditionalTypes] = useState<
    Set<'conditional' | 'non-conditional'>
  >(new Set(['conditional', 'non-conditional']));

  // Convert selected tag IDs to MultiSelectOption format
  const selectedTags: MultiSelectOption[] = useMemo(() => {
    return tagOptions.filter((option) => selectedTagIds.has(option.id));
  }, [tagOptions, selectedTagIds]);

  // Selected rules
  const selectedRules: MultiSelectOption[] = useMemo(() => {
    return (
      rebateRules
        ?.filter((rule) => selectedRuleIds.has(rule.id))
        .map((rule) => ({ id: rule.id, label: rule.name })) || []
    );
  }, [rebateRules, selectedRuleIds]);

  // Selected divisions
  const selectedDivisions: MultiSelectOption[] = useMemo(() => {
    return (
      divisions
        ?.filter((division) => selectedDivisionIds.has(division.id))
        .map((division) => ({
          id: division.id,
          label: division.name,
          group: division.tags?.[0]?.name,
        })) || []
    );
  }, [divisions, selectedDivisionIds]);

  // Convert selected conditional types to MultiSelectOption format
  const selectedRuleTypes: MultiSelectOption[] = useMemo(() => {
    const options: MultiSelectOption[] = [];
    if (selectedConditionalTypes.has('conditional')) {
      options.push({ id: '__conditional__', label: 'Conditional' });
    }
    if (selectedConditionalTypes.has('non-conditional')) {
      options.push({ id: '__non_conditional__', label: 'Non-Conditional' });
    }
    return options;
  }, [selectedConditionalTypes]);

  // Change handlers
  const handleTagsChange = (selectedTags: MultiSelectOption[]) => {
    const newTagIds = new Set(selectedTags.map((tag) => tag.id));
    setSelectedTagIds(newTagIds);
  };

  const handleRulesChange = (selectedRules: MultiSelectOption[]) => {
    const newRuleIds = new Set(selectedRules.map((rule) => rule.id));
    setSelectedRuleIds(newRuleIds);
  };

  const handleDivisionsChange = (selectedDivisions: MultiSelectOption[]) => {
    const newDivisionIds = new Set(
      selectedDivisions.map((division) => division.id),
    );
    setSelectedDivisionIds(newDivisionIds);
  };

  const handleRuleTypesChange = useCallback(
    (selectedTypes: MultiSelectOption[]) => {
      const conditionalTypes = new Set<'conditional' | 'non-conditional'>();
      selectedTypes.forEach((option) => {
        if (option.id === '__conditional__') {
          conditionalTypes.add('conditional');
        } else if (option.id === '__non_conditional__') {
          conditionalTypes.add('non-conditional');
        }
      });
      setSelectedConditionalTypes(conditionalTypes);
    },
    [],
  );

  // Build fast lookup sets for efficiency
  const selectedRuleIdSet = useMemo(
    () => new Set(selectedRuleIds),
    [selectedRuleIds],
  );

  const selectedDivisionIdSet = useMemo(
    () => new Set(selectedDivisionIds),
    [selectedDivisionIds],
  );

  // Build the final filter functions — AND between groups, OR within
  const filterFns = useMemo(
    () => ({
      // Rule filtering:
      // - by rule tags (filteredRuleIds from useRuleTagFiltering)
      // - AND by conditional/non-conditional type
      // - AND by explicitly selected rules
      rule: (bd: RebateBreakdown) => {
        // Tag filtering: if tags selected, must match filteredRuleIds
        const tagFilterPasses =
          selectedTagIds.size === 0 || filteredRuleIds.has(bd.ruleId);

        // Conditional/non-conditional filtering
        const isConditional = ruleIdToIsConditional.get(bd.ruleId) ?? false;
        const conditionalFilterPasses =
          (isConditional && selectedConditionalTypes.has('conditional')) ||
          (!isConditional && selectedConditionalTypes.has('non-conditional'));

        // Explicit rule selection filtering
        const explicitFilterPasses =
          selectedRuleIdSet.size === 0 || selectedRuleIdSet.has(bd.ruleId);

        // All filters must pass (AND logic)
        return (
          tagFilterPasses && conditionalFilterPasses && explicitFilterPasses
        );
      },

      // Division filtering (explicit only)
      division: (bd: RebateBreakdown) =>
        selectedDivisionIdSet.size === 0 ||
        selectedDivisionIdSet.has(bd.divisionId),
    }),
    [
      filteredRuleIds,
      selectedRuleIdSet,
      selectedDivisionIdSet,
      selectedTagIds,
      selectedConditionalTypes,
      ruleIdToIsConditional,
    ],
  );

  // Filter groups for UI
  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        label: 'Rule Tags',
        options: tagOptions,
        value: selectedTags,
        onChange: handleTagsChange,
        placeholder: 'Select rule tags...',
        searchPlaceholder: 'Search rule tags...',
      },
      {
        label: 'Rule Type',
        options: ruleTypeOptions,
        value: selectedRuleTypes,
        onChange: handleRuleTypesChange,
        placeholder: 'Select rule types...',
        searchPlaceholder: 'Search rule types...',
        error: selectedConditionalTypes.size === 0,
        shouldAutoSort: false,
      },
      {
        label: 'Rules',
        options: ruleOptions,
        value: selectedRules,
        onChange: handleRulesChange,
        placeholder: 'Select rules...',
        searchPlaceholder: 'Search rules...',
      },
      {
        label: 'Divisions',
        options: divisionOptions,
        value: selectedDivisions,
        onChange: handleDivisionsChange,
        placeholder: 'Select divisions...',
        searchPlaceholder: 'Search divisions...',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tagOptions,
      selectedTags,
      ruleTypeOptions,
      selectedRuleTypes,
      handleRuleTypesChange,
      selectedConditionalTypes.size,
      ruleOptions,
      selectedRules,
      divisionOptions,
      selectedDivisions,
    ],
  );

  // Active filter count and names
  const activeFilterCount = useMemo(() => {
    return (
      (selectedTagIds.size > 0 ? 1 : 0) +
      (selectedConditionalTypes.size < 2 ? 1 : 0) +
      (selectedDivisionIds.size > 0 ? 1 : 0) +
      (selectedRuleIds.size > 0 ? 1 : 0)
    );
  }, [
    selectedTagIds.size,
    selectedConditionalTypes.size,
    selectedDivisionIds.size,
    selectedRuleIds.size,
  ]);

  const activeFilterNames = useMemo(() => {
    const names: string[] = [];
    if (selectedTagIds.size > 0) names.push('Rule Tags');
    if (selectedConditionalTypes.size < 2) names.push('Rule Type');
    if (selectedRuleIds.size > 0) names.push('Rules');
    if (selectedDivisionIds.size > 0) names.push('Divisions');
    return names;
  }, [
    selectedTagIds.size,
    selectedConditionalTypes.size,
    selectedRuleIds.size,
    selectedDivisionIds.size,
  ]);

  // Clear all filters handler
  const clearAllFilters = () => {
    setSelectedTagIds(new Set());
    setSelectedDivisionIds(new Set());
    setSelectedRuleIds(new Set());
    setSelectedConditionalTypes(new Set(['conditional', 'non-conditional']));
  };

  return {
    filterFns,
    filterGroups,
    activeFilterCount,
    activeFilterNames,
    clearAllFilters,
    selectedTagIds,
    selectedRuleIds,
    selectedDivisionIds,
    selectedConditionalTypes,
  };
}
